import { useApolloClient, gql } from '@apollo/client';

import {
    GalleryUserStatus,
    type ContactStatus,
    type Gallery,
    type NewsPost,
    type Post,
} from 'src/types/__generated__/graphql';

type LikeOrDislikePostData = {
    id: Post['id'];
    totalLikes: Post['totalLikes'];
};

type LikeOrDislikeNewsPostData = {
    id: NewsPost['id'];
    totalLikes: NewsPost['totalLikes'];
};

type IncrementOrDecrementNumberOfContentsOnGalleryData = {
    id: Gallery['id'];
    numberOfContents: Gallery['numberOfContents'];
};

type IncrementOrDecrementNumberOfCommentsOnPostData = {
    id: Post['id'];
    totalComments: Post['totalComments'];
};

type IncrementOrDecrement = 'increment' | 'decrement';

const useApolloCacheUpdate = () => {
    const client = useApolloClient();

    const likeOrDislikePost = (post: LikeOrDislikePostData, liked: boolean) => {
        client.writeFragment({
            id: `Post:${post.id}`,
            fragment: gql`
                fragment PostLikedByCurrentUser on Post {
                    likedByCurrentUser
                    totalLikes
                }
            `,
            data: {
                likedByCurrentUser: liked,
                ...(post.totalLikes || post.totalLikes === 0
                    ? { totalLikes: liked ? post.totalLikes + 1 : post.totalLikes - 1 }
                    : {}),
            },
        });
    };

    const likeOrDislikeNewsPost = (newsPost: LikeOrDislikeNewsPostData, liked: boolean) => {
        client.writeFragment({
            id: `NewsPost:${newsPost.id}`,
            fragment: gql`
                fragment NewsPostLikedByCurrentUser on NewsPost {
                    likedByCurrentUser
                    totalLikes
                }
            `,
            data: {
                likedByCurrentUser: liked,
                ...(newsPost.totalLikes || newsPost.totalLikes === 0
                    ? { totalLikes: liked ? newsPost.totalLikes + 1 : newsPost.totalLikes - 1 }
                    : {}),
            },
        });
    };

    const addOrRemoveFavoritePost = (postId: string, favorite: boolean) => {
        client.writeFragment({
            id: `Post:${postId}`,
            fragment: gql`
                fragment PostIsUserFavorite on Post {
                    isUserFavorite
                }
            `,
            data: {
                isUserFavorite: favorite,
            },
        });
    };

    const addOrRemoveFavoriteAd = (adId: string, favorite: boolean) => {
        client.writeFragment({
            id: `Ad:${adId}`,
            fragment: gql`
                fragment AdIsUserFavorite on Ad {
                    isUserFavorite
                }
            `,
            data: {
                isUserFavorite: favorite,
            },
        });
    };

    const addOrRemoveFavoriteNewsPost = (newsPostId: string, favorite: boolean) => {
        client.writeFragment({
            id: `NewsPost:${newsPostId}`,
            fragment: gql`
                fragment NewsPostIsUserFavorite on NewsPost {
                    isUserFavorite
                }
            `,
            data: {
                isUserFavorite: favorite,
            },
        });
    };

    const incrementOrDecrementNumberOfContentsOnGallery = (
        gallery: IncrementOrDecrementNumberOfContentsOnGalleryData,
        increment: IncrementOrDecrement,
    ) => {
        client.writeFragment({
            id: `Gallery:${gallery.id}`,
            fragment: gql`
                fragment GalleryNumberOfContent on Gallery {
                    numberOfContents
                    contentLastUpdatedAt
                }
            `,
            data: {
                contentLastUpdatedAt: new Date().toISOString(),
                ...(gallery.numberOfContents || gallery.numberOfContents === 0
                    ? {
                          numberOfContents:
                              increment === 'increment' ? gallery.numberOfContents + 1 : gallery.numberOfContents - 1,
                      }
                    : {}),
            },
        });
    };

    const incrementOrDecrementNumberOfCommentsOnPost = (
        post: IncrementOrDecrementNumberOfCommentsOnPostData,
        increment: IncrementOrDecrement,
    ) => {
        client.writeFragment({
            id: `Post:${post.id}`,
            fragment: gql`
                fragment PostTotalComments on Post {
                    totalComments
                }
            `,
            data: {
                ...(post.totalComments || post.totalComments === 0
                    ? { totalComments: increment === 'increment' ? post.totalComments + 1 : post.totalComments - 1 }
                    : {}),
            },
        });
    };

    const followOrUnfollowUser = (userId: string, follow: boolean) => {
        client.writeFragment({
            id: `User:${userId}`,
            fragment: gql`
                fragment UserIsFollowed on User {
                    isFollowed
                }
            `,
            data: {
                isFollowed: follow,
            },
        });
    };

    const followOrUnfollowCompany = (companyId: string, follow: boolean) => {
        client.writeFragment({
            id: `Company:${companyId}`,
            fragment: gql`
                fragment CompanyIsFollowed on Company {
                    isFollowingCompany
                }
            `,
            data: {
                isFollowingCompany: follow,
            },
        });
    };

    const addOrRemoveContact = (
        userId: string,
        addOrRemove: 'add' | 'remove',
        lastContactStatus?: ContactStatus | null,
    ) => {
        let newContactStatus;
        switch (lastContactStatus) {
            case 'uninvited':
                // in this case we can only have the add button and if we click we go to the pending state
                newContactStatus = addOrRemove === 'add' ? 'pending' : null;
                break;
            case 'to_respond':
                // in this case we can only have the add button and if we click we go to the accepted state
                newContactStatus = addOrRemove === 'add' ? 'accepted' : null;
                break;
            case 'refused':
                // Comment from backend (so we never have this case):
                // If our invitation is refused or we have refused an invitation, we can send a new invitation,
                // it's like there was no invitation so we return UNINVITED
                break;
            case 'accepted':
                // in this case we can only have the remove button and if we click we go back to the uninvited state
                newContactStatus = addOrRemove === 'remove' ? 'uninvited' : null;
                break;
            case 'pending':
                // can't add or remove button if we have the pending state
                break;
            default:
                // means no contactStatus, in this case we can only have the add button and if we click we go to the pending state
                newContactStatus = addOrRemove === 'add' ? 'pending' : null;
                break;
        }
        client.writeFragment({
            id: `User:${userId}`,
            fragment: gql`
                fragment UserContactStatus on User {
                    contactStatusV2
                }
            `,
            data: {
                contactStatusV2: newContactStatus,
            },
        });
    };

    const setGalleryUserStatus = (galleryUserId: string, status: GalleryUserStatus) => {
        client.writeFragment({
            id: `GalleryUser:${galleryUserId}`,
            fragment: gql`
                fragment GalleryUserStatus on GalleryUser {
                    status
                }
            `,
            data: {
                status: GalleryUserStatus.Invited,
            },
        });
    };

    return {
        likePostInCache: (post: LikeOrDislikePostData) => likeOrDislikePost(post, true),
        dislikePostInCache: (post: LikeOrDislikePostData) => likeOrDislikePost(post, false),
        likeNewsPostInCache: (newsPost: LikeOrDislikeNewsPostData) => likeOrDislikeNewsPost(newsPost, true),
        dislikeNewsPostInCache: (newsPost: LikeOrDislikeNewsPostData) => likeOrDislikeNewsPost(newsPost, false),
        addFavoritePostInCache: (postId: string) => addOrRemoveFavoritePost(postId, true),
        removeFavoritePostInCache: (postId: string) => addOrRemoveFavoritePost(postId, false),
        addFavoriteAdInCache: (adId: string) => addOrRemoveFavoriteAd(adId, true),
        removeFavoriteAdInCache: (adId: string) => addOrRemoveFavoriteAd(adId, false),
        addFavoriteNewsPostInCache: (newsPostId: string) => addOrRemoveFavoriteNewsPost(newsPostId, true),
        removeFavoriteNewsPostInCache: (newsPostId: string) => addOrRemoveFavoriteNewsPost(newsPostId, false),
        incrementNumberOfContentsOnGalleryInCache: (gallery: IncrementOrDecrementNumberOfContentsOnGalleryData) =>
            incrementOrDecrementNumberOfContentsOnGallery(gallery, 'increment'),
        decrementNumberOfContentsOnGalleryInCache: (gallery: IncrementOrDecrementNumberOfContentsOnGalleryData) =>
            incrementOrDecrementNumberOfContentsOnGallery(gallery, 'decrement'),
        incrementNumberOfCommentsOnPostInCache: (post: IncrementOrDecrementNumberOfCommentsOnPostData) =>
            incrementOrDecrementNumberOfCommentsOnPost(post, 'increment'),
        decrementNumberOfCommentsOnPostInCache: (post: IncrementOrDecrementNumberOfCommentsOnPostData) =>
            incrementOrDecrementNumberOfCommentsOnPost(post, 'decrement'),
        followUser: (userId: string) => followOrUnfollowUser(userId, true),
        unfollowUser: (userId: string) => followOrUnfollowUser(userId, false),
        followCompany: (companyId: string) => followOrUnfollowCompany(companyId, true),
        unfollowCompany: (companyId: string) => followOrUnfollowCompany(companyId, false),
        addContactInCache: (userId: string, lastContactStatus?: ContactStatus | null) =>
            addOrRemoveContact(userId, 'add', lastContactStatus),
        removeContactInCache: (userId: string, lastContactStatus?: ContactStatus | null) =>
            addOrRemoveContact(userId, 'remove', lastContactStatus),
        setGalleryUserStatusToInvitedInCache: (galleryUserId: string) =>
            setGalleryUserStatus(galleryUserId, GalleryUserStatus.Invited),
    };
};

export default useApolloCacheUpdate;
